import React from 'react';
import { makeStyles, TableCell } from '@material-ui/core';
import CheckCircleRoundedIcon from '@material-ui/icons/CheckCircleRounded';
import HelpOutlineRoundedIcon from '@material-ui/icons/HelpOutlineRounded';
import RemoveCircleOutlineRoundedIcon from '@material-ui/icons/RemoveCircleOutlineRounded';
import EditRoundedIcon from '@material-ui/icons/EditRounded';

import { unoTheme } from '../../assets/styles/unoTheme';
import { RATE_STATUS, RATE_STATUS_LABEL, MANUAL_RATE_LABEL, formatRate, getRateTitle } from './costsUtils';

//Célula de taxa da tabela de Custos: valor quando informado, mais um ícone que comunica o
//estado semântico. Cada estado usa uma FORMA diferente, não só uma cor — a cor sozinha não
//pode carregar significado. O motivo detalhado vai para title/aria-label.
const useStyles = makeStyles(() => ({
    content: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        //espaçamento por irmão adjacente em vez de gap, que não é suportado em navegadores
        //antigos de flexbox
        '& > * + *': {
            marginLeft: '4px',
        },
    },
    value: {
        whiteSpace: 'nowrap',
    },
    icon: {
        fontSize: '14px',
        flexShrink: 0,
    },
    informed: {
        color: unoTheme.successColor,
    },
    notInformed: {
        color: unoTheme.warningColor,
    },
    notApplicable: {
        color: unoTheme.neutralIconColor,
    },
    manual: {
        color: unoTheme.mainColor,
    },
}));

//Ícone de taxa ajustada manualmente (client_fund_rates); exportado para a legenda
export function ManualRateIcon() {
    const classes = useStyles();

    return <EditRoundedIcon className={`${classes.icon} ${classes.manual}`} />;
}

//Tooltip da taxa ajustada: deixa visível o que a fonte informaria sem o ajuste
function getManualRateTitle(rate) {
    const api = rate.apiRate;
    const apiLabel = api && api.status === RATE_STATUS.INFORMED
        ? formatRate(api.value)
        : RATE_STATUS_LABEL[(api && api.status) || RATE_STATUS.NOT_INFORMED].toLowerCase();

    return `${MANUAL_RATE_LABEL} (fonte: ${apiLabel})`;
}

//Ícone do estado, exportado à parte para que a legenda use exatamente os mesmos glifos
//e as mesmas cores das células.
export function RateStatusIcon({ status }) {
    const classes = useStyles();

    if (status === RATE_STATUS.INFORMED) {
        return <CheckCircleRoundedIcon className={`${classes.icon} ${classes.informed}`} />;
    }

    if (status === RATE_STATUS.NOT_APPLICABLE) {
        return <RemoveCircleOutlineRoundedIcon className={`${classes.icon} ${classes.notApplicable}`} />;
    }

    return <HelpOutlineRoundedIcon className={`${classes.icon} ${classes.notInformed}`} />;
}

export default function RateCell({ rate, align = 'center' }) {
    const classes = useStyles();

    const status = (rate && rate.status) || RATE_STATUS.NOT_INFORMED;
    const reason = rate ? rate.reason : null;
    const isManual = !!(rate && rate.manual);
    const title = isManual ? getManualRateTitle(rate) : getRateTitle(status, reason);
    const label = formatRate(rate ? rate.value : null);

    return (
        <TableCell align={align}>
            {/* um único nome acessível no wrapper: o ícone fica aria-hidden (padrão do MUI
                quando não recebe titleAccess), evitando tooltip e leitura duplicados */}
            <span className={classes.content} role="img" title={title} aria-label={title}>
                {label ? <span className={classes.value}>{label}</span> : null}
                {isManual ? <ManualRateIcon /> : <RateStatusIcon status={status} />}
            </span>
        </TableCell>
    );
}
